import { Layer } from 'shared/map-exports';
import { usePoisState } from 'state/usePoisState';
import { MapClickGetPois, MapLayerProps } from 'features/mapping/mapping.types';
import { ElectricalLinePoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import { addVisible } from 'shared/addVisible';
import { getWatchdutyStaticTileUrl } from 'features/mapping/components/layers/layers.utils';
import { useStaticTileCacheDomain } from 'features/mapping/hooks/useStaticTileCacheDomain';
import { i18n } from 'i18n/config';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { ElectricLineType } from './types';
import {
  ELECTRICAL_MAPTILES_DIRECTORY,
  ELECTRICAL_MAP_INTERACTIVE_LAYER_ID,
  ELECTRICAL_MAP_LAYER_ID,
  ELECTRICAL_SOURCE_DATA_LAYER,
} from './constants';
import {
  getSelectedStrokeStyle,
  electricalLayerStyle,
  layerStyleLineCasingElectrical,
  getInteractiveLayerStyle,
} from './layerStyles';

export const electricLineClickHandler: MapClickGetPois<ElectricLineType> = (
  geoJsonFeatures,
) => {
  return geoJsonFeatures.map((feature) => {
    const electricLine: ElectricLineType = feature.properties;
    const metadata: ElectricalLinePoi = {
      title: i18n.t('map.poi.electricalLines.title'),
      list: [
        {
          name: i18n.t('map.poi.electricalLines.status'),
          value: electricLine.STATUS,
        },
        {
          name: i18n.t('map.poi.electricalLines.type'),
          value: electricLine.TYPE,
        },
        {
          name: i18n.t('map.poi.electricalLines.voltClass'),
          value: `${electricLine.VOLT_CLASS} kV`,
        },
        {
          name: i18n.t('map.poi.electricalLines.voltage'),
          value: `${electricLine.VOLTAGE} kV`,
        },
        {
          name: i18n.t('map.poi.electricalLines.owner'),
          value: electricLine.OWNER,
        },
      ],
    };

    return {
      type: 'electricalLine',
      id: electricLine.ID,
      metadata,
    };
  });
};

export const ElectricLinesLayer = (props: MapLayerProps): JSX.Element => {
  const { visible } = props;
  const { selectedPoi } = usePoisState();
  const staticTileCacheDomain = useStaticTileCacheDomain();

  const selectedLineUId =
    (selectedPoi?.type === 'electricalLine' && selectedPoi.id) || '';

  const selectedStrokeStyle = getSelectedStrokeStyle({
    selectedId: selectedLineUId,
    sourceLayer: ELECTRICAL_SOURCE_DATA_LAYER,
  });

  return (
    <VisibilityToggledSource
      visible={visible}
      url={getWatchdutyStaticTileUrl(
        ELECTRICAL_MAPTILES_DIRECTORY,
        staticTileCacheDomain,
      )}
    >
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(layerStyleLineCasingElectrical, visible)}
      />
      <Layer
        id={ELECTRICAL_MAP_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(electricalLayerStyle, visible)}
      />
      <Layer
        id={ELECTRICAL_MAP_INTERACTIVE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(
          getInteractiveLayerStyle(ELECTRICAL_SOURCE_DATA_LAYER),
          visible,
        )}
      />
      <Layer
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(selectedStrokeStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};
